import React from 'react';
import {
  CgBreakdownShell,
  CgBreakdownSection,
  CgBreakdownRow,
  CgBreakdownStep,
  CgBreakdownResult,
  formatBreakdownCurrency,
} from 'modules/Common/costBreakdown';
import { toFixedNonZero } from 'helpers/numberFormat';
import Decimal from 'decimal.js';

// Deliberately NOT the shared convention-aware number formatter: this is a
// public share page, whose figures are read by whoever holds the link and are
// therefore rendered plainly, ungrouped, in one form for every viewer.
const fmtN = (v, dp = 3) =>
  v == null || !Number.isFinite(v) ? '-' : toFixedNonZero(new Decimal(v), dp);

const fmtCur = (v, dp = 2) =>
  v == null || !Number.isFinite(v)
    ? '-'
    : formatBreakdownCurrency(v, { currency: 'EUR', decimals: dp });

/**
 * Calculation breakdown for the CBAM Cost / t cell on the public share page.
 * Composed from the shared `modules/Common/costBreakdown` vocabulary (D23) and
 * trimmed to what's relevant on a public catalog (no granularity badges, no
 * actual-vs-default split — there's only one row). Its rendered output is
 * pinned byte-for-byte by `CbamCostBreakdown.domIdentity.test.jsx`.
 *
 * Inputs travel through two steps:
 *   1. Chargeable emissions = (Direct or Total SEE) − SEFA
 *   2. CBAM Cost / t = Chargeable × Certificate Price
 *
 * SEFA arrives precomputed from the backend, so we present it as a given input
 * rather than re-deriving it from a benchmark × CBAM factor.
 */
export default function CbamCostBreakdown({
  see,
  basisLabel = 'Total SEE',
  sefa,
  carbonPrice,
  cost,
}) {
  const chargeable = see != null && sefa != null ? Math.max(0, see - sefa) : null;

  return (
    <CgBreakdownShell
      title="CBAM Cost / t — Breakdown"
      className="min-w-[320px]"
    >
      {/* SEFA input */}
      <CgBreakdownSection
        title={'SEFA '}
        titleSuffix={
          <span className="text-gray-500 font-normal">(Specific Embedded Free Allocation)</span>
        }
        bodyClassName="bg-gray-50 rounded-lg p-2"
      >
        <CgBreakdownRow
          label="SEFA:"
          valueClassName="font-medium text-purple-700"
          value={`${fmtN(sefa)} tCO₂e/t`}
        />
      </CgBreakdownSection>

      {/* Step 1 — Chargeable */}
      <CgBreakdownSection
        title="Chargeable Emissions"
        bodyClassName="bg-purple-50 rounded-lg p-2.5 space-y-1.5"
      >
        <CgBreakdownStep
          pill="Step 1"
          label={`${basisLabel} − SEFA`}
          className={null}
          headerClassName="flex items-center gap-1 text-gray-600 text-xs"
        >
          <p className="text-xs text-gray-800 font-medium">
            {fmtN(see)} − {fmtN(sefa)}
            <CgBreakdownResult>{fmtN(chargeable)} tCO₂e/t</CgBreakdownResult>
          </p>
        </CgBreakdownStep>
      </CgBreakdownSection>

      {/* Step 2 — Cost */}
      <CgBreakdownSection
        title="CBAM Cost / t"
        bodyClassName="bg-purple-50 rounded-lg p-2.5 space-y-1.5"
      >
        <CgBreakdownStep
          pill="Step 2"
          label="× Certificate Price"
          className={null}
          headerClassName="flex items-center gap-1 text-gray-600 text-xs"
        >
          <p className="text-xs text-gray-800 font-medium">
            {fmtN(chargeable)} × {fmtCur(carbonPrice)}
            <CgBreakdownResult className="text-orange-600 font-semibold">
              {fmtCur(cost)}/t
            </CgBreakdownResult>
          </p>
        </CgBreakdownStep>
      </CgBreakdownSection>

      {/* Inputs footer */}
      <CgBreakdownSection className="border-t border-gray-200 pt-2 space-y-1">
        <CgBreakdownRow
          className="flex justify-between text-[11px] text-gray-500"
          labelClassName={null}
          valueClassName={null}
          label={`${basisLabel}:`}
          value={`${fmtN(see)} tCO₂e/t`}
        />
        <CgBreakdownRow
          className="flex justify-between text-[11px] text-gray-500"
          labelClassName={null}
          valueClassName={null}
          label="Certificate Price:"
          value={`${fmtCur(carbonPrice)}/tCO₂e`}
        />
      </CgBreakdownSection>
    </CgBreakdownShell>
  );
}
